import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

interface HttpServiceConfig {
  params?: HttpParams;
  urlPostfix?: string;
  mapResponse?: (res) => void;
}

export const mapResponse = <T>(config?: HttpServiceConfig): any =>
  map((res) =>
    config && !!config?.mapResponse ? config?.mapResponse(res) : res
  );

/**
 * @classdesc Servicio genérico que ayuda a extender la funcionalidad de servicios HTTP específicos.
 */
export class HttpGenericService {
  protected httpHeaders: HttpHeaders;

  /**
   * @description Inicializa los HTTP headers y las URL's del recurso.
   * @param httpClient Clase que permite realizar peticiones HTTP.
   * @param baseUrl La url base de un recurso específico. Representa toda la url de la API hasta su raíz.
   * @param endpoint El endpoint de un recurso específico. Representa el controlador de la API.
   */
  constructor(
    protected httpClient: HttpClient,
    @Inject(String) protected baseUrl: string,
    @Inject(String) protected endpoint: string
  ) {
    this.httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json');
  }

  /**
   * @description Realiza una búsqueda avanzada bajo unos parámetros proporcionados.
   */
  getAll<T>(config?: HttpServiceConfig): Observable<T> {
    const { params, urlPostfix } = this.mergeConfig(config);
    const url = this.resolveUrl(urlPostfix ? urlPostfix : '');
    return this.httpClient.get<T>(url, { params }).pipe(mapResponse<T>(config));
  }

  /**
   * @description Realiza una búsqueda avanzada bajo unos parámetros proporcionados y el id de la entidad.
   */
  getById<T>(id: string, config?: HttpServiceConfig): Observable<T> {
    const { urlPostfix } = this.mergeConfig(config);
    const url = this.resolveUrl(urlPostfix ? urlPostfix : '', id);
    return this.httpClient.get<T>(url).pipe(mapResponse<T>(config));
  }

  /**
   * @description Envía una petición POST al recurso seleccionado.
   */
  post<T>(body: T, config?: HttpServiceConfig): Observable<T> {
    const { urlPostfix } = this.mergeConfig(config);
    const url = this.resolveUrl(urlPostfix ? urlPostfix : '');
    return this.httpClient

      .post<T>(url, body, { headers: this.httpHeaders })

      .pipe(mapResponse<T>(config));
  }

  /**
   * @description Envía una petición PATCH al recurso seleccionado.
   */
  patch<T>(
    id: string,
    body: Partial<T>,
    config?: HttpServiceConfig
  ): Observable<T> {
    const { urlPostfix } = this.mergeConfig(config);
    const url = this.resolveUrl(id, urlPostfix ? urlPostfix : '');
    return this.httpClient

      .patch<T>(url, body, { headers: this.httpHeaders })

      .pipe(mapResponse<T>(config));
  }

  /**
   * @description Envía una petición PUT al recurso seleccionado.
   */
  put<T>(
    id: string,
    body: Partial<T>,
    config?: HttpServiceConfig
  ): Observable<T> {
    const { urlPostfix } = this.mergeConfig(config);
    const url = this.resolveUrl(id, urlPostfix ? urlPostfix : '');
    return this.httpClient

      .put<T>(url, body, { headers: this.httpHeaders })

      .pipe(mapResponse<T>(config));
  }

  /**
   * @description Envía una petición DELETE al recurso seleccionado.
   */
  delete<T>(id: string, config?: HttpServiceConfig): Observable<T> {
    const { urlPostfix } = this.mergeConfig(config);
    const url = this.resolveUrl(id, urlPostfix ? urlPostfix : '');
    return this.httpClient.delete<T>(url).pipe(mapResponse<T>(config));
  }

  private resolveUrl(...segments: string[]): string {
    return `${this.baseUrl}/${this.endpoint}/${segments.join('/')}`;
  }

  private mergeConfig(config?: HttpServiceConfig): HttpServiceConfig {
    return config || {};
  }
}
